import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import ProductionRouteAssets from './components/ProductionRouteAssets'
import Definition from './components/ProductionRouteDefinition'
import ProductionRouteHeat from './components/ProductionRouteHeat'
import ProductionRouteWasteGas from './components/ProductionRouteWasteGas'
import ProductionRouteElectricity from './components/ProductionRouteElectricity'
import ProductionRouteStoredCO2 from './components/ProductionRouteStoredCO2'
import {
  HeatFlowSystems,
  StoredCO2Handling,
  ProductionRouteCreatePayload,
  ProductionRouteCreationState,
  productionRouteCreatePayloadSchema,
  RouteProductionMethod,
  ProductionRouteAsset
} from '@/features/production-routes/api/ProductionRouteModel'
import ProductionRouteCreationSummaryPage from './components/ProductionRouteCreationSummary'
import { SelectedElectricityProductionMethodsPayload } from '@/features/production-routes/api/ElectricityProductionMethodsModel'
import ProductionRouteLayout from './layout/ProductionRouteLayout'
import { useCreateProductionRoute } from '@/shared/hooks/api/productionRoutes'
import { useInstallation } from '@/features/installations/hooks/useInstallations'
import { LoadingScreen } from '@/shared/components'
import { useSession } from '@/shared/hooks/useSession'
import { AggregatedGoodsCategory } from '@/common/config/productionMethods'

function loadProductionRoute(localStorageKey: string): ProductionRouteCreatePayload | null {
  const productionRoute = window.localStorage.getItem(localStorageKey)
  if (productionRoute) {
    const parsedProductionRoute = JSON.parse(productionRoute) as ProductionRouteCreatePayload
    return parsedProductionRoute
  }
  return null
}

export default function ProductionRouteCreate({ localStorageKey }: { localStorageKey: string }) {
  const installationId = useParams<{ installationId: string }>().installationId!
  const {
    session: {
      organization: { id: organizationId }
    }
  } = useSession()
  const {
    isLoading,
    isSuccess,
    data: installation
  } = useInstallation(organizationId, installationId)

  const navigate = useNavigate()

  const goodsFamily = installation?.goodsFamily

  const [productionRoute, setProductionRoute] = useState<ProductionRouteCreationState>(
    loadProductionRoute(localStorageKey) || {
      installationId: installationId,
      name: '',
      aggregatedGood: '',
      assets: [],
      heatFlowSystem: null,
      heatFlowLimestonePurification: null,
      heatFlowSodaAshPurification: null,
      isUsingWasteGas: null,
      electricityProductionMethods: [],
      storedCO2Handling: null,
      productionMethods: []
    }
  )

  const isIntegrated = productionRoute.productionMethods.length > 1
  const isIronAndSteel = installation?.goodsFamily === 'IRONANDSTEEL'
  const isUsingEmissionPoints =
    !isIronAndSteel ||
    productionRoute.aggregatedGood === AggregatedGoodsCategory.IRON_OR_STEEL_PRODUCTS

  const setDefinition = useCallback(
    (payload: { aggregatedGood: string; productionMethods: RouteProductionMethod[] }) => {
      setProductionRoute({
        ...productionRoute,
        ...payload
      })
    },
    [productionRoute]
  )

  const { mutateAsync: createProductionRouteMutation, isPending: isCreatingProductionRoute } =
    useCreateProductionRoute(organizationId)

  const saveAndNavigateToProductionRoutes = useCallback(async () => {
    const result = productionRouteCreatePayloadSchema.safeParse(productionRoute)
    if (!result.success) {
      return
    }

    await createProductionRouteMutation(result.data)
    navigate(`/dashboard/${installationId}`)
  }, [createProductionRouteMutation, productionRoute, navigate, installationId])

  const onDefinitionCompleted = (payload: {
    aggregatedGood: string
    productionMethods: RouteProductionMethod[]
  }) => {
    setDefinition(payload)
    if (
      !isIronAndSteel ||
      payload.aggregatedGood === AggregatedGoodsCategory.IRON_OR_STEEL_PRODUCTS
    ) {
      navigate('../emission-points')
    } else {
      navigate('../heat')
    }
  }

  const onHeatCompleted = (payload: {
    heatFlowSystem: HeatFlowSystems
    heatFlowSodaAshPurification: boolean
    heatFlowLimestonePurification: boolean
  }) => {
    setProductionRoute({
      ...productionRoute,
      heatFlowSystem: payload.heatFlowSystem,
      heatFlowSodaAshPurification: payload.heatFlowSodaAshPurification,
      heatFlowLimestonePurification: payload.heatFlowLimestonePurification
    })

    navigate('../electricity')
  }

  const onWasteGasCompleted = (payload: { isUsingWasteGas: boolean }) => {
    setProductionRoute({
      ...productionRoute,
      isUsingWasteGas: payload.isUsingWasteGas
    })

    navigate('../stored-co2')
  }

  const onStoredCO2Completed = (payload: { storedCO2Handling: StoredCO2Handling }) => {
    setProductionRoute({
      ...productionRoute,
      storedCO2Handling: payload.storedCO2Handling
    })

    navigate('../summary')
  }

  const onEmissionPointsCompleted = (selectedAssets: ProductionRouteAsset[]) => {
    setProductionRoute({
      ...productionRoute,
      assets: selectedAssets
    })

    navigate('../heat')
  }

  const onElectricityCompleted = ({
    electricityProductionMethods
  }: {
    electricityProductionMethods: SelectedElectricityProductionMethodsPayload
  }) => {
    setProductionRoute({
      ...productionRoute,
      electricityProductionMethods
    })

    navigate('../waste-gas')
  }

  useEffect(() => {
    const stringifiedProductionRoute = JSON.stringify(productionRoute)
    window.localStorage.setItem(localStorageKey, stringifiedProductionRoute)
  }, [productionRoute, localStorageKey])

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isSuccess) {
    return <Navigate to="/dashboard" />
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProductionRouteLayout
            state={productionRoute}
            isUsingEmissionPoints={isUsingEmissionPoints}
          />
        }
      >
        <Route
          path="/definition"
          element={
            <Definition
              next={onDefinitionCompleted}
              goodsFamily={goodsFamily}
              initialState={{
                aggregatedGood: productionRoute.aggregatedGood,
                productionMethods: productionRoute.productionMethods
              }}
            />
          }
        />
        <Route
          path="/emission-points"
          element={
            <ProductionRouteAssets
              assets={productionRoute.assets}
              next={onEmissionPointsCompleted}
              previous={() => navigate('../definition')}
              productionMethodCode={productionRoute.productionMethods[0]?.code}
            />
          }
        />
        <Route
          path="/heat"
          element={
            <ProductionRouteHeat
              next={onHeatCompleted}
              previous={() => {
                if (isIntegrated) {
                  navigate('../definition')
                } else {
                  navigate('../emission-points')
                }
              }}
              initialValues={{
                heatFlowSystem: productionRoute.heatFlowSystem,
                heatFlowSodaAshPurification: productionRoute.heatFlowSodaAshPurification,
                heatFlowLimestonePurification: productionRoute.heatFlowLimestonePurification
              }}
            />
          }
        />
        <Route
          path="/electricity"
          element={
            <ProductionRouteElectricity
              initialValue={productionRoute.electricityProductionMethods}
              next={onElectricityCompleted}
              previous={() => navigate('../heat')}
            />
          }
        />
        <Route
          path="/waste-gas"
          element={
            <ProductionRouteWasteGas
              next={onWasteGasCompleted}
              previous={() => navigate('../electricity')}
              initialValues={{
                isUsingWasteGas: productionRoute.isUsingWasteGas
              }}
            />
          }
        />
        <Route
          path="/stored-co2"
          element={
            <ProductionRouteStoredCO2
              initialValue={productionRoute.storedCO2Handling}
              next={onStoredCO2Completed}
              previous={() => navigate('../waste-gas')}
            />
          }
        />

        <Route
          path="/summary"
          element={
            <ProductionRouteCreationSummaryPage
              payload={productionRoute}
              isCreatingProductionRoute={isCreatingProductionRoute}
              createProductionRouteAndNavigateToDashboard={saveAndNavigateToProductionRoutes}
              previous={() => navigate('stored-co2')}
            />
          }
        />
        <Route index element={<Navigate to="../definition" />} />
      </Route>
    </Routes>
  )
}
